import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';
import { publicAPI } from '../services/api';

// Optimize image URLs for faster loading
const optimizeImageUrl = (url, width = 600) => {
  if (!url) return url;
  if (url.includes('unsplash.com')) {
    const baseUrl = url.split('?')[0];
    return `${baseUrl}?w=${width}&q=75&auto=format&fit=crop`;
  }
  if (url.includes('pexels.com')) {
    return url.includes('?') ? `${url}&w=${width}` : `${url}?w=${width}`;
  }
  return url;
};

const GalleryPage = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await publicAPI.getGallery();
        setGalleryImages(response.data);
      } catch (err) {
        console.error('Error fetching gallery:', err);
        setError('Unable to load gallery images');
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-32 pb-16 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#007897]" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-32 pb-16 text-center">
          <p className="text-gray-600">{error}</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#007897] to-[#006680]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Gallery</h1>
            <p className="text-blue-100 max-w-2xl mx-auto">
              Take a virtual tour of our state-of-the-art diagnostic facilities.
            </p>
            <div className="flex items-center justify-center gap-2 mt-6 text-sm">
              <Link to="/" className="text-blue-200 hover:text-white">Home</Link>
              <span className="text-blue-300">/</span>
              <span>Gallery</span>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image) => (
              <div
                key={image.id}
                className="group relative overflow-hidden rounded-2xl cursor-pointer bg-gray-100"
                onClick={() => image.image && setSelectedImage(image)}
              >
                {image.image ? (
                  <img
                    src={optimizeImageUrl(image.image, 600)}
                    alt={image.title}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => { 
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="256" viewBox="0 0 400 256"><rect fill="%23f3f4f6" width="400" height="256"/><rect x="160" y="80" width="80" height="60" rx="4" fill="%23d1d5db"/><circle cx="180" cy="100" r="10" fill="%23f3f4f6"/><path d="M160 140 L190 110 L220 130 L240 115 L240 140 Z" fill="%239ca3af"/><text fill="%239ca3af" font-family="sans-serif" font-size="12" x="50%25" y="85%25" text-anchor="middle">Image</text></svg>';
                    }}
                  />
                ) : (
                  <div className="w-full h-64 flex flex-col items-center justify-center text-gray-400">
                    <svg className="w-16 h-16 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                    <span className="text-sm">{image.title}</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-lg font-semibold">{image.title}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl w-full">
            <img
              src={selectedImage.image}
              alt={selectedImage.title}
              className="w-full h-auto rounded-lg"
            />
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent rounded-b-lg">
              <h3 className="text-xl font-semibold text-white">{selectedImage.title}</h3>
            </div>
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default GalleryPage;
